/* eslint-disable no-nested-ternary */

"use client"

import useEmblaCarousel from "embla-carousel-react"
import React, { useEffect, useRef, useCallback, useState } from "react"

import { AllEventsCarouselProps } from "@/types/events"
import AllEventSlides from "@/ui/components/events/AllEventSlides"
import PageGrid from "@/ui/components/PageGrid"
import SplitTextL from "@/ui/patterns/SplitTextLeft"
// import Seo from '../components/Seo'

import ArrowCarousel from "../../../../public/icons/arrow-carousel.svg"

const AllEventsCarousel: React.FC<AllEventsCarouselProps> = ({
  data,
  ...divProps
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    skipSnaps: false,
    dragFree: false,
    duration: 20,
    slidesToScroll: 1,
  })
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const divRef = useRef<HTMLInputElement>(null)

  // Set to 32 since this is the minimum left margin spacing
  const [leftMargin, setLeftMargin] = React.useState(32)

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((api: any) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  useEffect(() => {
    function handleResize() {
      // This function is used to calculate the offset needed from the Carousel section
      // We set an id to the wrapper which is using the PageGrid grid
      // We then set the margin-left to the inner carousel wrapper
      // This auto updates on load and resize
      const offset = divRef.current?.offsetLeft
      // eslint-disable-next-line no-unused-expressions, no-void
      offset !== undefined && void setLeftMargin(offset)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  })
  return (
    <div {...divProps}>
      <PageGrid>
        <div className="col-span-full" ref={divRef} />
      </PageGrid>

      {data?.length > 0 ? (
        <>
          <SplitTextL
            title="Upcoming Events"
            wrapperClassLeft="pt-10 lg:pt-28 pb-10 "
            hasButton
            buttonTo="/events/all/"
            buttonCTA="Browse All"
          />
          <div className="pt-8 pb-10 md:pb-24 xl:pt-12 lg:pt-10">
            <div
              className="pl-4 pr-8 md:pr-0"
              style={{ marginLeft: leftMargin }}
            >
              {/* Embla viewport */}
              <div className="embla" ref={emblaRef}>
                <div className="embla__container">
                  <AllEventSlides
                    className="col-span-full md:col-span-2 lg:col-span-4"
                    data={data}
                  />
                </div>
              </div>

              {data.length >= 3 && (
                <div className=" md:mt-4 relative z-10">
                  <button
                    onClick={scrollPrev}
                    disabled={!canScrollPrev}
                    className="disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50 cursor-pointer relative z-10"
                    type="button"
                    aria-label="Previous slide"
                  >
                    <ArrowCarousel className="mr-10 transform -rotate-180 h-14 w-14" />
                  </button>
                  <button
                    onClick={scrollNext}
                    disabled={!canScrollNext}
                    className="disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50 cursor-pointer relative z-10"
                    type="button"
                    aria-label="Next slide"
                  >
                    <ArrowCarousel className="h-14 w-14" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        ""
      )}
    </div>
  )
}

export default AllEventsCarousel
